import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, PushPin, Trash, PencilSimple, Image as ImageIcon, X, Plus } from "@phosphor-icons/react";

/**
 * /admin/signals — admin composer + list for the Signals feed.
 */
const TARGETS = [
  { id: "all",     label: "Everyone" },
  { id: "country", label: "By country" },
  { id: "segment", label: "By segment" },
  { id: "users",   label: "Specific users" },
];

const SEGMENTS = [
  { id: "all",         label: "All" },
  { id: "nk_plus",     label: "NK+ subscribers" },
  { id: "top100",      label: "Top 100 (this season)" },
  { id: "champions",   label: "Championship title holders" },
  { id: "new_players", label: "New players (< 5 games)" },
];

const TYPES = ["announcement", "update", "event", "championship", "prize", "mode"];

export default function AdminSignals() {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/signals?limit=200").then((r) => setRows(r.data?.items || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const remove = async (sid) => {
    if (!confirm("Deactivate this signal? Players will stop seeing it.")) return;
    try {
      await api.delete(`/admin/signals/${sid}`);
      toast.success("Signal deactivated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-5xl mx-auto" data-testid="admin-signals-root">
      <div className="flex items-center justify-between">
        <div>
          <p className="eyebrow mb-1">Communications</p>
          <h1 className="font-display text-2xl sm:text-3xl font-medium text-white">Signals</h1>
          <p className="text-sm text-white/55 mt-1">Broadcast updates, championships, and announcements to players.</p>
        </div>
        <button
          data-testid="admin-signals-new-button"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="inline-flex items-center gap-2 bg-nk-gold text-nk-charcoal px-4 py-2 rounded-md font-medium text-sm hover:bg-nk-gold/85 transition-colors"
        >
          <Plus size={14} weight="bold" /> New signal
        </button>
      </div>

      {showForm && (
        <SignalForm
          initial={editing}
          busy={busy}
          onSubmit={async (payload) => {
            setBusy(true);
            try {
              if (editing) {
                await api.patch(`/admin/signals/${editing.signal_id}`, payload);
                toast.success("Signal updated");
              } else {
                await api.post("/admin/signals", payload);
                toast.success("Signal published");
              }
              setShowForm(false);
              setEditing(null);
              load();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Save failed");
            } finally {
              setBusy(false);
            }
          }}
          onCancel={() => { setShowForm(false); setEditing(null); }}
        />
      )}

      <div className="space-y-3" data-testid="admin-signals-list">
        {rows.length === 0 && !showForm && (
          <div className="nk-card p-10 text-center">
            <Bell size={36} weight="duotone" className="text-white/25 mx-auto mb-3" />
            <p className="text-white/70">No signals yet</p>
            <p className="text-xs text-white/45 mt-1">Click <em>New signal</em> above to broadcast the first update.</p>
          </div>
        )}
        {rows.map((s) => (
          <div key={s.signal_id} className={`nk-card p-4 ${s.active ? "" : "opacity-50"}`} data-testid={`admin-signal-row-${s.signal_id}`}>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-md bg-nk-gold/15 text-nk-gold flex items-center justify-center shrink-0">
                <Bell size={14} weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  {s.pinned && <PushPin size={10} weight="fill" className="text-nk-gold" />}
                  <span className="text-[9px] uppercase tracking-widest text-white/40">{s.signal_type}</span>
                  <span className="text-[9px] uppercase tracking-widest text-white/40">·</span>
                  <span className="text-[9px] uppercase tracking-widest text-nk-gold/70">{audienceLabel(s)}</span>
                </div>
                <p className="text-sm font-medium text-white truncate">{s.title}</p>
                <p className="text-[11px] text-white/55 line-clamp-2 mt-0.5">{s.body}</p>
                <p className="text-[10px] text-white/35 mt-1 font-mono">
                  {s.published_at ? new Date(s.published_at).toLocaleString() : "—"}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => { setEditing(s); setShowForm(true); }}
                  className="p-1.5 rounded hover:bg-white/8 text-white/60 hover:text-nk-gold"
                  data-testid={`admin-signal-edit-${s.signal_id}`}
                  aria-label="Edit"
                >
                  <PencilSimple size={13} />
                </button>
                <button
                  onClick={() => remove(s.signal_id)}
                  className="p-1.5 rounded hover:bg-white/8 text-white/60 hover:text-nk-bad"
                  data-testid={`admin-signal-delete-${s.signal_id}`}
                  aria-label="Deactivate"
                >
                  <Trash size={13} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function audienceLabel(s) {
  const t = s.target;
  if (t === "all") return "everyone";
  if (t === "country") return `country: ${(s.country_codes || []).join(", ") || "—"}`;
  if (t === "segment") return `segment: ${(s.segments || []).join(", ") || "—"}`;
  if (t === "users")   return `${(s.user_ids || []).length} user(s)`;
  return t;
}

/* ==================== FORM ==================== */

function SignalForm({ initial, onSubmit, onCancel, busy }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [summary, setSummary] = useState(initial?.summary || "");
  const [heroEmoji, setHeroEmoji] = useState(initial?.hero_emoji || "");
  const [body, setBody] = useState(initial?.body || "");
  const [signalType, setSignalType] = useState(initial?.signal_type || "announcement");
  const [target, setTarget] = useState(initial?.target || "all");
  const [countryCodes, setCountryCodes] = useState((initial?.country_codes || []).join(","));
  const [segments, setSegments] = useState(initial?.segments || []);
  const [userIds, setUserIds] = useState((initial?.user_ids || []).join(","));
  const [pinned, setPinned] = useState(!!initial?.pinned);
  const [ctaLabel, setCtaLabel] = useState(initial?.cta_label || "");
  const [ctaHref, setCtaHref] = useState(initial?.cta_href || "");
  const [imageDataUrl, setImageDataUrl] = useState(initial?.image_data_url || "");
  const [preview, setPreview] = useState(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Image too large (max 2 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setImageDataUrl(reader.result);
    reader.readAsDataURL(f);
  };

  const requestPreview = async () => {
    try {
      const payload = buildPayload();
      const r = await api.post("/admin/signals/preview", payload);
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const buildPayload = () => ({
    title: title.trim(),
    body: body.trim(),
    summary: summary.trim() || null,
    hero_emoji: heroEmoji.trim() || null,
    image_data_url: imageDataUrl || null,
    signal_type: signalType,
    target,
    country_codes: target === "country" ? countryCodes.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) : [],
    segments: target === "segment" ? segments : [],
    user_ids: target === "users" ? userIds.split(",").map((s) => s.trim()).filter(Boolean) : [],
    pinned,
    cta_label: ctaLabel.trim() || null,
    cta_href: ctaHref.trim() || null,
  });

  const submit = (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return toast.error("Title and body are required");
    onSubmit(buildPayload());
  };

  return (
    <form onSubmit={submit} className="nk-card p-5 space-y-4" data-testid="admin-signal-form">
      <div className="flex items-center justify-between">
        <p className="eyebrow">{initial ? "Edit signal" : "New signal"}</p>
        <button type="button" onClick={onCancel} className="text-white/50 hover:text-white" aria-label="Close">
          <X size={16} />
        </button>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-widest text-white/55 block mb-1">Title</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={140}
          required
          data-testid="admin-signal-title"
          className="w-full bg-black/25 border border-white/12 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:border-nk-gold/50"
          placeholder="A short, punchy headline"
        />
      </div>

      <div className="grid grid-cols-[80px_1fr] gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/55 block mb-1">Emoji</label>
          <input
            value={heroEmoji}
            onChange={(e) => setHeroEmoji(e.target.value)}
            maxLength={4}
            data-testid="admin-signal-emoji"
            className="w-full bg-black/25 border border-white/12 rounded-sm px-3 py-2 text-2xl text-center focus:outline-none focus:border-nk-gold/50"
            placeholder="✨"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/55 block mb-1">Summary (shown on feed card)</label>
          <input
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            maxLength={240}
            data-testid="admin-signal-summary"
            className="w-full bg-black/25 border border-white/12 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:border-nk-gold/50"
            placeholder="One-sentence teaser (auto-derived if empty)"
          />
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-widest text-white/55 block mb-1">Body (full detail view)</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={8000}
          required
          rows={5}
          data-testid="admin-signal-body"
          className="w-full bg-black/25 border border-white/12 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:border-nk-gold/50 resize-y"
          placeholder="Full body. Use blank lines between paragraphs. Use `── SECTION TITLE ──` to split sections."
        />
        <p className="text-[10px] text-white/35 mt-1">{body.length} / 8000</p>
      </div>

      {/* Image */}
      <div>
        <label className="text-[10px] uppercase tracking-widest text-white/55 block mb-2">Image (optional · max 2 MB)</label>
        <div className="flex items-start gap-3">
          <label className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/12 hover:border-nk-gold/40 rounded-sm text-xs text-white/70 hover:text-nk-gold cursor-pointer transition-colors">
            <ImageIcon size={13} /> Upload
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onFile} className="hidden" data-testid="admin-signal-image-input" />
          </label>
          {imageDataUrl && (
            <div className="relative">
              {/* eslint-disable-next-line */}
              <img src={imageDataUrl} alt="preview" className="h-16 rounded border border-white/15" />
              <button
                type="button"
                onClick={() => setImageDataUrl("")}
                className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-nk-bad text-white text-[10px]"
                aria-label="Remove image"
              >×</button>
            </div>
          )}
        </div>
      </div>

      {/* Type & Pinned */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/55 block mb-1">Type</label>
          <select
            value={signalType}
            onChange={(e) => setSignalType(e.target.value)}
            data-testid="admin-signal-type"
            className="w-full bg-black/25 border border-white/12 rounded-sm px-3 py-2 text-sm text-white"
          >
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <label className="inline-flex items-center gap-2 text-sm text-white/75 cursor-pointer">
            <input
              type="checkbox"
              checked={pinned}
              onChange={(e) => setPinned(e.target.checked)}
              data-testid="admin-signal-pinned"
              className="accent-nk-gold"
            /> Pin to top of feed
          </label>
        </div>
      </div>

      {/* Target */}
      <div>
        <label className="text-[10px] uppercase tracking-widest text-white/55 block mb-2">Audience</label>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {TARGETS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTarget(t.id)}
              data-testid={`admin-signal-target-${t.id}`}
              className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest ${
                target === t.id
                  ? "bg-nk-gold text-nk-charcoal"
                  : "border border-white/12 text-white/70 hover:border-nk-gold/40 hover:text-nk-gold"
              }`}
            >{t.label}</button>
          ))}
        </div>
        {target === "country" && (
          <input
            value={countryCodes}
            onChange={(e) => setCountryCodes(e.target.value)}
            placeholder="Country codes, comma-separated (e.g. GH, NG, KE)"
            data-testid="admin-signal-countries"
            className="w-full bg-black/25 border border-white/12 rounded-sm px-3 py-2 text-sm text-white"
          />
        )}
        {target === "segment" && (
          <div className="flex flex-wrap gap-1.5">
            {SEGMENTS.map((s) => {
              const active = segments.includes(s.id);
              return (
                <button
                  type="button"
                  key={s.id}
                  onClick={() => setSegments((cur) => active ? cur.filter((x) => x !== s.id) : [...cur, s.id])}
                  data-testid={`admin-signal-segment-${s.id}`}
                  className={`px-3 py-1.5 rounded-full text-[11px] ${
                    active ? "bg-nk-gold text-nk-charcoal" : "border border-white/12 text-white/70"
                  }`}
                >{s.label}</button>
              );
            })}
          </div>
        )}
        {target === "users" && (
          <input
            value={userIds}
            onChange={(e) => setUserIds(e.target.value)}
            placeholder="User IDs, comma-separated"
            data-testid="admin-signal-user-ids"
            className="w-full bg-black/25 border border-white/12 rounded-sm px-3 py-2 text-sm text-white"
          />
        )}
      </div>

      {/* CTA */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/55 block mb-1">CTA label (optional)</label>
          <input
            value={ctaLabel}
            onChange={(e) => setCtaLabel(e.target.value)}
            placeholder="e.g. Play now"
            data-testid="admin-signal-cta-label"
            className="w-full bg-black/25 border border-white/12 rounded-sm px-3 py-2 text-sm text-white"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/55 block mb-1">CTA link</label>
          <input
            value={ctaHref}
            onChange={(e) => setCtaHref(e.target.value)}
            placeholder="/daily"
            data-testid="admin-signal-cta-href"
            className="w-full bg-black/25 border border-white/12 rounded-sm px-3 py-2 text-sm text-white"
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 pt-1">
        <button
          type="button"
          onClick={requestPreview}
          data-testid="admin-signal-preview"
          className="text-[11px] uppercase tracking-widest text-white/60 hover:text-nk-gold"
        >Estimate audience</button>
        {preview && (
          <span className="text-[11px] text-white/60 font-mono">
            ~{preview.estimated_audience?.toLocaleString?.() ?? preview.estimated_audience} players
          </span>
        )}
        <div className="flex items-center gap-2">
          <button type="button" onClick={onCancel} className="text-xs text-white/60 hover:text-white px-3 py-2">Cancel</button>
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-signal-submit"
            className="bg-nk-gold text-nk-charcoal px-4 py-2 rounded-md text-sm font-medium hover:bg-nk-gold/85 disabled:opacity-50"
          >{busy ? "Saving…" : initial ? "Save changes" : "Publish signal"}</button>
        </div>
      </div>
    </form>
  );
}
